import { useSlice } from "@bridge-applications/provider-kit";
import { useEffect } from "react";
import { authenticatedApiHeaders } from "../helpers/api";
import {
    careerAccessFromHeaders,
    careerAccessState,
    isLocalCareerAuthoring,
} from "../helpers/careerAccess";

const LOCAL_AUTHORING = isLocalCareerAuthoring(
    import.meta.env.DEV,
    import.meta.env.VITE_CAREER_CONTENT_SOURCE,
);

export function useCareerAccess() {
    const { state: authState } = useSlice("authSlice");
    return careerAccessState(authState, LOCAL_AUTHORING);
}

/** Mounted once in Layout; access metadata is never persisted across sessions. */
export function useCareerAccessRuntime(): void {
    const { state: authState, setSlice: setAuthSlice } = useSlice("authSlice");
    const { bootstrapStatus, userId } = authState;

    useEffect(() => {
        if (LOCAL_AUTHORING) return;
        setAuthSlice({ careerAccess: null });
        if (bootstrapStatus !== "ready" || userId === null) return;

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 5_000);
        let cancelled = false;
        const commit = (allowed: boolean): void => {
            if (cancelled) return;
            setAuthSlice((current) => {
                if (
                    current.bootstrapStatus !== "ready" ||
                    current.userId !== userId
                ) {
                    return {};
                }
                return { careerAccess: { userId, allowed } };
            });
        };

        void authenticatedApiHeaders("/api/v1/sm/state", controller.signal)
            .then((headers) =>
                commit(careerAccessFromHeaders(headers, userId).allowed),
            )
            .catch(() => commit(false))
            .finally(() => window.clearTimeout(timeout));

        return () => {
            cancelled = true;
            controller.abort();
            window.clearTimeout(timeout);
        };
    }, [bootstrapStatus, setAuthSlice, userId]);
}
